<script setup>
  import { ref, watchEffect, onMounted, computed} from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useUserStore } from '@/stores/UserStore'
  import BaseModal from './base/BaseModal.vue';
  import ModalContentTextInput from '@/components/ModalContentTextInput.vue';
  import { useToaster } from '@/composables/useToaster';
  
  const emits = defineEmits(['close']);

  const props = defineProps({
    profileId: {
      type: Number,
      required: false
    },
    show: {
      type: Boolean,
      default: false
    },    
  });

  const userStore = useUserStore();
  const toaster = useToaster();

  const { t } = useI18n();

  const savedName = ref(t('profiles.copyProfile'));
  const showModal = ref(false);

  const profileId = computed(() => props.profileId || userStore.currentProfileId);
  const profileName = computed(() => userStore.getProfileById(profileId.value)?.name || userStore.currentProfile.name || 'current')

  function closeModal() {
    showModal.value = false;

    emits('close'); 
  }

  async function handleSaveProfile() {
    const data = await userStore.copyProfile(profileId.value, savedName.value);
    
    if (data.status_code !== 0) {

      toaster.toast({
        type: 'warning',
        content: t('profiles.copyErrorToast', { name: profileName.value })
      })

      return;
    };

    const newProfileId = data.status_message;
    
    await userStore.getProfiles(true);

    const profile = userStore.getProfileById(newProfileId);

    // Check containers for maps that show favorite spots. They will have a profileId prop in marker overlay options.
    // If the profileId prop is present, but the profileId doesn't exist in the user's profiles, update the container options to use the newly created profileId.
    for (let i = 0; i < profile.containers.length; i++) {
      const container = profile.containers[i];

      if (container.container_type_id !== 'Map') continue;

      const options = JSON.parse(container.options);

      if (!Object.hasOwnProperty.call(options, 'overlays')) continue;
      if (!Object.hasOwnProperty.call(options.overlays, 'markers')) continue;
      if (!Object.hasOwnProperty.call(options.overlays.markers, 'profileId')) continue;

      const userHasSavedMapProfileId = userStore.getProfileById(options.overlays.markers.profileId) !== undefined;

      if (userHasSavedMapProfileId) continue;

      options.overlays.markers.profileId = newProfileId;
      await userStore.updateContainer(container.container_id, container.container_name, JSON.stringify(options));
    }

    toaster.toast({
      type: 'success',
      content: t('profiles.copiedToast', { name: profileName.value, newName: savedName.value })
    })

    closeModal();
  }

  watchEffect(() => {
    savedName.value = t('profiles.copyDefaultName', { name: profileName.value });
  });

  onMounted(() => {
    // Placing the watchEffect in the onMounted callback makes sure
    // the BaseModal has time to run the modal open animation by delaying the 
    // showModal value being set to true until after this component mounts
    watchEffect(() => showModal.value = props.show);
  });  

</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="false"
  >
    <ModalContentTextInput
      v-model="savedName"
      :modalTitle="`Copy: ${profileName}`"
      labelText="Name"
      acceptButtonText="Save"
      @accept="handleSaveProfile"
      @cancel="closeModal"
    /> 
  </BaseModal>
</template>